import {SupportedLocales} from '#types/locale.js';

export const metricsBuilderLanguage = {
  [SupportedLocales.EN]: {
    pages: {
      list: {
        title: 'Metrics',
      },
      create: {
        title: 'Create New Metric',
      },
      edit: {
        title: 'Edit Metric',
      },
    },
    buttons: {
      newMetric: 'New Metric',
      backToMetrics: 'Back to Metrics',
      create: 'Create',
      creating: 'Creating...',
      update: 'Update Metric',
      updating: 'Updating...',
      delete: 'Delete',
      deleting: 'Deleting...',
      cancel: 'Cancel',
      clearSelection: 'Clear selection',
      deleteSelected: 'Delete selected',
      preview: 'Preview',
      tryAgain: 'Try Again',
    },
    table: {
      columns: {
        metricName: 'Metric Name',
        source: 'Source',
        createdAt: 'Created at',
        createdBy: 'Created by',
      },
      search: 'Search',
      selectedCount: (count: number) => `Selected ${count} metrics`,
    },
    form: {
      sections: {
        basicInfo: 'Basic Information',
        dataSource: 'Data Source',
        metricDefinition: 'Metric Definition',
        filters: 'Filters',
        preview: 'Preview',
      },
      labels: {
        name: 'Name',
        description: 'Description',
        optional: '(optional)',
        sourceConnection: 'Source Connection',
        datasetGroup: 'Dataset Group',
        dataset: 'Dataset',
        metricValueColumn: 'Metric Value Column',
        aggregation: 'Aggregation',
        optimization: 'Optimization',
        timestampColumn: 'Timestamp Column',
      },
      placeholders: {
        name: 'Total Revenue',
        description: 'Describe what this metric measures...',
        loading: 'Loading...',
        selectSourceConnection: 'Select source connection',
        selectDatasetGroup: 'Select dataset group',
        selectDataset: 'Select dataset',
        selectColumn: 'Select column',
        selectAggregation: 'Select aggregation',
        selectOptimization: 'Select optimization',
        previewMetric: 'Preview metric',
      },
      filterActions: {
        add: 'Add a filter',
        incomplete: 'Incomplete filters will not be applied.',
        insert: 'Insert filter',
        insertAbove: 'Insert above',
        insertBelow: 'Insert below',
        manage: 'Open manage menu',
      },
      aggregations: {
        sum: 'Sum',
        count: 'Count',
        avg: 'Average',
        min: 'Minimum',
        max: 'Maximum',
      },
      optimizations: {
        maximize: 'Maximize',
        minimize: 'Minimize',
      },
      errors: {
        nameRequired: 'Name is required',
        sourceConnectionRequired: 'Source connection is required',
        datasetGroupRequired: 'Dataset group is required',
        datasetRequired: 'Dataset is required',
        datasetOrGroupRequired:
          'Either dataset group or dataset must be provided',
        datasetAndGroupConflict:
          'Cannot provide both dataset group and dataset',
        tableRequired: 'Table is required',
        metricColumnRequired: 'Metric column is required',
        aggregationRequired: 'Aggregation type is required',
        timestampColumnRequired: 'Timestamp column is required',
      },
    },
    dialogs: {
      deleteMetric: {
        title: 'Delete metric',
        description: (name: string) =>
          `Are you sure you want to delete "${name}"? This action cannot be undone.`,
      },
      deleteMetrics: {
        title: 'Delete metrics',
        description:
          'Are you sure you want to delete these metrics? This action cannot be undone.',
      },
      updateMetric: {
        title: 'Update metric',
        warning: 'Warning:',
        description:
          'Any changes to this metric will break future reporting. Historical data will not be recalculated with the new configuration.',
      },
    },
    toasts: {
      error: 'Error',
      deleteSuccess: 'Metric deleted successfully!',
      deleteError: 'Failed to delete metric. Please try again.',
      bulkDeleteSuccess: 'Metrics deleted successfully!',
      bulkDeleteError: 'Failed to delete metrics. Please try again.',
      updateSuccess: 'Metric updated successfully!',
      updateError: 'Failed to update metric. Please try again.',
      createSuccess: 'Metric created successfully!',
      createError: 'Failed to create metric. Please try again.',
      notFound: 'Metric not found. Please try again.',
    },
    report: {
      densityChart: {
        ariaLabel: 'Metric density curve',
        densityAxisLabel: 'Density',
        emptyState: 'No recent density data available.',
        outlierNote: (count: number) =>
          `${count.toLocaleString()} ${
            count === 1 ? 'value' : 'values'
          } outside chart range`,
        valueAxisLabel: 'Value',
      },
      suitability: {
        ariaLabel: 'Metric suitability',
        experimentation: 'Experimentation',
        journeyDecisioning: 'Decisioning in Journeys',
        limitedRecentData: (name: string) =>
          `Metric can be used for ${name}, but has not seen a lot of movement recently.`,
        noRecentData: (name: string) =>
          `Metric does not have recent data and is not appropriate for ${name}.`,
        noTimestamp: (name: string) =>
          `Metric needs a timestamp column to evaluate recent data for ${name}.`,
        queryFailed: (name: string) =>
          `Metric recent data could not be evaluated for ${name}.`,
        recentUserCount: (count: number, days: number) =>
          `${count.toLocaleString()} individuals accrued it in the last ${days} days.`,
        significantRecentData: (name: string) =>
          `This is a good metric for ${name}, with significant recent data to exploit.`,
      },
      columnTypes: {
        user_id_column: 'Identifies the unique user for this metric',
        metric_value_column:
          'The column containing the metric value to aggregate',
        group_by_column:
          'The column used to group metric values (e.g., by date)',
        unknown: 'This column was not mapped to a specific metric role',
      },
      emptyState: 'No data available for the selected configuration.',
      errorTitle: 'Failed to load preview data',
      tabs: {
        results: 'Results',
        sql: 'SQL',
      },
      sql: {
        ariaLabel: 'Compiled metric SQL',
        emptyState: 'SQL will appear after you preview this metric.',
      },
    },
    errors: {
      metricNotFound: 'Metric not found',
    },
  },
  [SupportedLocales.ES]: {
    pages: {
      list: {
        title: 'Métricas',
      },
      create: {
        title: 'Crear Nueva Métrica',
      },
      edit: {
        title: 'Editar Métrica',
      },
    },
    buttons: {
      newMetric: 'Nueva Métrica',
      backToMetrics: 'Volver a Métricas',
      create: 'Crear',
      creating: 'Creando...',
      update: 'Actualizar Métrica',
      updating: 'Actualizando...',
      delete: 'Eliminar',
      deleting: 'Eliminando...',
      cancel: 'Cancelar',
      clearSelection: 'Limpiar selección',
      deleteSelected: 'Eliminar seleccionados',
      preview: 'Vista previa',
      tryAgain: 'Intentar de nuevo',
    },
    table: {
      columns: {
        metricName: 'Nombre de la Métrica',
        source: 'Fuente',
        createdAt: 'Creado el',
        createdBy: 'Creado por',
      },
      search: 'Buscar',
      selectedCount: (count: number) => `${count} métricas seleccionadas`,
    },
    form: {
      sections: {
        basicInfo: 'Información Básica',
        dataSource: 'Fuente de Datos',
        metricDefinition: 'Definición de Métrica',
        filters: 'Filtros',
        preview: 'Vista Previa',
      },
      labels: {
        name: 'Nombre',
        description: 'Descripción',
        optional: '(opcional)',
        sourceConnection: 'Conexión de Origen',
        datasetGroup: 'Grupo de Datos',
        dataset: 'Conjunto de Datos',
        metricValueColumn: 'Columna de Valor de Métrica',
        aggregation: 'Agregación',
        optimization: 'Optimización',
        timestampColumn: 'Columna de Timestamp',
      },
      placeholders: {
        name: 'Ingresos Totales',
        description: 'Describe qué mide esta métrica...',
        loading: 'Cargando...',
        selectSourceConnection: 'Seleccionar conexión de origen',
        selectDatasetGroup: 'Seleccionar grupo de datos',
        selectDataset: 'Seleccionar conjunto de datos',
        selectColumn: 'Seleccionar columna',
        selectAggregation: 'Seleccionar agregación',
        selectOptimization: 'Seleccionar optimización',
        previewMetric: 'Vista previa de métrica',
      },
      filterActions: {
        add: 'Agregar un filtro',
        incomplete: 'Los filtros incompletos no se aplicarán.',
        insert: 'Insertar filtro',
        insertAbove: 'Insertar arriba',
        insertBelow: 'Insertar abajo',
        manage: 'Abrir menú de administración',
      },
      aggregations: {
        sum: 'Suma',
        count: 'Conteo',
        avg: 'Promedio',
        min: 'Mínimo',
        max: 'Máximo',
      },
      optimizations: {
        maximize: 'Maximizar',
        minimize: 'Minimizar',
      },
      errors: {
        nameRequired: 'El nombre es requerido',
        sourceConnectionRequired: 'La conexión de origen es requerida',
        datasetGroupRequired: 'El grupo de datos es requerido',
        datasetRequired: 'El conjunto de datos es requerido',
        datasetOrGroupRequired:
          'Se debe proporcionar el grupo de datos o el conjunto de datos',
        datasetAndGroupConflict:
          'No se pueden proporcionar tanto el grupo de datos como el conjunto de datos',
        tableRequired: 'La tabla es requerida',
        metricColumnRequired: 'La columna de métrica es requerida',
        aggregationRequired: 'El tipo de agregación es requerido',
        timestampColumnRequired: 'La columna de timestamp es requerida',
      },
    },
    dialogs: {
      deleteMetric: {
        title: 'Eliminar métrica',
        description: (name: string) =>
          `¿Estás seguro de que deseas eliminar "${name}"? Esta acción no se puede deshacer.`,
      },
      deleteMetrics: {
        title: 'Eliminar métricas',
        description:
          '¿Estás seguro de que deseas eliminar estas métricas? Esta acción no se puede deshacer.',
      },
      updateMetric: {
        title: 'Actualizar métrica',
        warning: 'Advertencia:',
        description:
          'Cualquier cambio en esta métrica afectará los reportes futuros. Los datos históricos no serán recalculados con la nueva configuración.',
      },
    },
    toasts: {
      error: 'Error',
      deleteSuccess: '¡Métrica eliminada exitosamente!',
      deleteError: 'Error al eliminar la métrica. Por favor intenta de nuevo.',
      bulkDeleteSuccess: '¡Métricas eliminadas exitosamente!',
      bulkDeleteError:
        'Error al eliminar las métricas. Por favor intenta de nuevo.',
      updateSuccess: '¡Métrica actualizada exitosamente!',
      updateError:
        'Error al actualizar la métrica. Por favor intenta de nuevo.',
      createSuccess: '¡Métrica creada exitosamente!',
      createError: 'Error al crear la métrica. Por favor intenta de nuevo.',
      notFound: 'Métrica no encontrada. Por favor intenta de nuevo.',
    },
    report: {
      densityChart: {
        ariaLabel: 'Curva de densidad de la métrica',
        densityAxisLabel: 'Densidad',
        emptyState: 'No hay datos recientes de densidad disponibles.',
        outlierNote: (count: number) =>
          `${count.toLocaleString()} ${
            count === 1 ? 'valor' : 'valores'
          } fuera del rango del gráfico`,
        valueAxisLabel: 'Valor',
      },
      suitability: {
        ariaLabel: 'Idoneidad de la métrica',
        experimentation: 'Experimentación',
        journeyDecisioning: 'Toma de decisiones en Journeys',
        limitedRecentData: (name: string) =>
          `La métrica se puede usar para ${name}, pero no ha tenido mucho movimiento recientemente.`,
        noRecentData: (name: string) =>
          `La métrica no tiene datos recientes y no es apropiada para ${name}.`,
        noTimestamp: (name: string) =>
          `La métrica necesita una columna de timestamp para evaluar datos recientes para ${name}.`,
        queryFailed: (name: string) =>
          `No se pudieron evaluar los datos recientes de la métrica para ${name}.`,
        recentUserCount: (count: number, days: number) =>
          `${count.toLocaleString()} individuos la acumularon en los últimos ${days} días.`,
        significantRecentData: (name: string) =>
          `Esta es una buena métrica para ${name}, con datos recientes significativos para aprovechar.`,
      },
      columnTypes: {
        user_id_column: 'Identifica al usuario único para esta métrica',
        metric_value_column:
          'La columna que contiene el valor de la métrica a agregar',
        group_by_column:
          'La columna utilizada para agrupar valores de métrica (ej., por fecha)',
        unknown: 'Esta columna no fue asignada a un rol específico de métrica',
      },
      emptyState:
        'No hay datos disponibles para la configuración seleccionada.',
      errorTitle: 'Error al cargar los datos de vista previa',
      tabs: {
        results: 'Resultados',
        sql: 'SQL',
      },
      sql: {
        ariaLabel: 'SQL compilado de la métrica',
        emptyState:
          'El SQL aparecerá después de generar la vista previa de esta métrica.',
      },
    },
    errors: {
      metricNotFound: 'Métrica no encontrada',
    },
  },
};
